import type { WholesaleRule } from '@/shared/utils/wholesale-pricing';
import type { AddonCategory } from './addons';
import type { QuantityPromotionRule } from '@/shared/utils/quantity-promotion';

export const ENDPOINT = 'api/app/merchants';

export type StoreItem = {
  id: string;
  name: string;
  /**
   * Categoria do produto (PRAT-184): usada para saber se ele participa da
   * promoção por quantidade. Opcional para respostas antigas.
   */
  category_id?: string | null;
  description: string | null;
  /**
   * Preço cheio/original do produto ("De"). Sempre presente. Quando há
   * promoção, este é o valor exibido riscado ao lado do preço de venda.
   */
  price: string;
  /**
   * Valor do desconto abatido do preço cheio (quanto se desconta), não o preço
   * final. Opcional: ausente/nulo quando não há promoção. O preço de venda
   * ("Por") efetivamente cobrado é `price − discount`, calculado em
   * `utils/promotion.ts`. A validação (número finito, > 0 e < `price`) é feita
   * lá para evitar promoções inválidas/negativas.
   */
  discount?: string | null;
  /**
   * Sinaliza que o produto é popular ("Mais pedidos"). Informativo e opcional:
   * as categorias dinâmicas (Populares/Novidades/Promoções) agora chegam prontas
   * no metadado `categories` do backend, não sendo mais montadas no cliente.
   */
  is_popular?: boolean | number | string | null;
  /**
   * Sinaliza que o produto é novidade. Informativo e opcional; ver observação em
   * `is_popular` sobre as categorias dinâmicas vindas do backend.
   */
  is_new?: boolean | number | string | null;
  image: string | null;
  addon_categories: AddonCategory[];
  /**
   * Tipo do produto (PRAT-20): `2` = combo. Opcional para compatibilidade com
   * respostas antigas; o que decide a montagem do combo é `combo_groups`.
   */
  type?: number | string;
  /**
   * Grupos de escolha do combo (PRAT-20). Vazio/ausente em produto comum e em
   * combo fixo (sem escolha) — nesses casos o fluxo segue exatamente o de hoje.
   */
  combo_groups?: ComboGroup[];
  /**
   * Tipo do combo (PRAT-20): `'fixed'` (itens fixos) ou `'choice'` (grupos de
   * escolha). `null`/ausente = fixo. Um combo é de um tipo só.
   */
  combo_mode?: 'fixed' | 'choice' | null;
  /**
   * Produtos fixos do combo (PRAT-79): o que sempre vem no combo fixo, para a
   * tela do produto mostrar "Neste combo". Vazio/ausente nos demais.
   */
  combo_items?: ComboFixedItem[];
};

/** Produto que sempre vem num combo fixo (PRAT-79). */
export type ComboFixedItem = {
  item_id: string;
  name: string;
  description?: string | null;
  image?: string | null;
  quantity: number;
};

/** Opção de um grupo de escolha do combo (um produto que pode ser escolhido). */
export type ComboGroupOption = {
  id: string;
  item_id: string; // produto escolhido (vai em `combo_choices[].item_id`)
  name: string;
  description: string | null;
  image: string | null;
  quantity: number; // unidades do produto que a opção entrega
  additional_price: number | string; // acréscimo no preço do combo (decimal)
  /** Complementos do produto; `[]` quando o grupo não é personalizável. */
  addon_categories: AddonCategory[];
};

/** Grupo de escolha do combo (ex.: "Pizza — escolha 1"). */
export type ComboGroup = {
  id: string;
  name: string;
  min_choices: number;
  max_choices: number; // 0 = sem teto
  is_customizable: boolean | number;
  order_column?: number;
  options: ComboGroupOption[];
};

export type StoreItems = StoreItem[];

export type StoreItemsByCategory = {
  [category: string]: StoreItems;
};

/**
 * Etiqueta exibida ao lado do título de uma categoria (ex.: "Mais pedidos",
 * "Novidades"). Usada principalmente pelas categorias virtuais geradas pela
 * ordenação dinâmica do cardápio.
 */
export type CategoryTag = {
  label: string; // texto da etiqueta
  color?: string | null; // cor opcional (hex) usada como destaque da etiqueta
};

/**
 * Metadado opcional de uma categoria do cardápio. Enviado pelo backend para
 * descrever a ordem de exibição e as categorias virtuais (dinâmicas), como
 * "Mais pedidos" ou "Promoções". Quando ausente, a UI faz fallback para a
 * listagem simples derivada de `items`.
 */
export type CategoryMeta = {
  name: string; // deve casar com a chave correspondente em `items`
  is_virtual?: boolean; // true quando a categoria é gerada dinamicamente
  tag?: CategoryTag | null; // etiqueta exibida junto ao título
};

export type GetStoreItemsResponse = {
  recommended: [];
  items: StoreItemsByCategory;
  categories?: CategoryMeta[]; // metadados de ordenação/categorias virtuais (opcional)
  quantity_promotion?: QuantityPromotionRule | null; // promoção por quantidade da loja (PRAT-184)
  wholesale_pricing?: WholesaleRule | null; // atacado por faixa (PRAT-187); null = loja sem atacado
};

export type GetStoreItemsRequest = {
  slug: string; // identificador do estabelecimento que vende os produtos
  // PRAT-125: 'balcao' lista os produtos de "Mesas e Balcão" (atendimento), na
  // ordem do lojista; sem canal, os de "Delivery (App)".
  canal?: 'balcao';
};
